import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, apiErr } from "../lib/api";
import { PortalNav } from "../components/PortalNav";
import { discountLabel, orderWithCoupon } from "../components/ProximityOffers";
import { toast } from "sonner";
import { Wallet as WalletIcon, Tag, Trash2, ArrowRight, Loader2, Check } from "lucide-react";

export default function Wallet() {
  const nav = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/account/coupons").then((r) => setCoupons(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    try { await api.delete(`/account/coupons/${id}`); setCoupons((p) => p.filter((c) => c.id !== id)); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-white">
      <PortalNav />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-head text-3xl font-extrabold flex items-center gap-2"><WalletIcon className="text-[#FFC400]" /> Portafoglio coupon</h1>
            <p className="text-white/55 text-sm mt-1">Le offerte di prossimità che hai salvato.</p>
          </div>
          <Link to="/account" className="text-sm font-semibold text-[#FFC400] hover:underline">← Il mio account</Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#FFC400]" /></div>
        ) : coupons.length === 0 ? (
          <div className="text-center py-16 rounded-2xl border border-white/10 bg-white/[0.03]" data-testid="wallet-empty">
            <Tag className="mx-auto text-white/30 mb-3" size={32} />
            <p className="text-white/60">Nessun coupon salvato.</p>
            <p className="text-white/40 text-sm mt-1">Dopo un ordine, salva le offerte dei locali vicini per usarle quando vuoi.</p>
            <Link to="/" className="inline-block mt-4 text-[#FFC400] font-semibold hover:underline">Scopri i locali →</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="wallet-coupons">
            {coupons.map((c) => (
              <motion.div key={c.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                className={`rounded-2xl border border-dashed p-4 flex flex-col gap-3 ${c.used ? "opacity-60" : ""} border-[#FFC400]/40 bg-[#FFC400]/[0.07]`}
                data-testid={`wallet-coupon-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{c.headline}</p>
                    <p className="text-xs text-white/55 truncate">{c.source === "welcome" ? "Valido su tutti i locali Pickfood" : `${c.store_name} · ${c.brand}`}</p>
                  </div>
                  <span className="shrink-0 text-xs font-bold px-2.5 py-1 rounded-full bg-[#FFC400] text-[#0B0B0D]">{discountLabel(c)}</span>
                </div>
                <span className="text-[11px] font-mono font-bold px-2 py-1 rounded bg-[#FFC400]/20 text-[#FFC400] self-start">{c.coupon_code}{c.min_order ? ` · min €${Number(c.min_order).toFixed(2)}` : ""}</span>
                <div className="flex items-center justify-between gap-2 mt-auto">
                  <button onClick={() => del(c.id)} className="text-white/40 hover:text-red-400" data-testid={`wallet-del-${c.id}`}><Trash2 size={16} /></button>
                  {c.used ? (
                    <span className="text-xs font-semibold text-green-300 inline-flex items-center gap-1"><Check size={14} /> Utilizzato</span>
                  ) : c.source === "welcome" ? (
                    <Link to="/" data-testid={`wallet-explore-${c.id}`}
                      className="h-9 px-4 rounded-full text-xs font-semibold text-[#0B0B0D] inline-flex items-center gap-1 bg-[#FFC400] hover:bg-[#E6AC00]">
                      Scopri i locali <ArrowRight size={14} />
                    </Link>
                  ) : (
                    <button onClick={() => orderWithCoupon(nav, c)} data-testid={`wallet-order-${c.id}`}
                      className="h-9 px-4 rounded-full text-xs font-semibold text-[#0B0B0D] inline-flex items-center gap-1 bg-[#FFC400] hover:bg-[#E6AC00]">
                      Ordina ora <ArrowRight size={14} />
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
